"use client";

import { useEffect } from "react";
import { useAuthStore } from "@/stores/auth-store";
import {
  ACTIVITY_STORAGE_KEY,
  currentActivityHour,
  shouldRecordActivity,
} from "@/lib/activity/record-activity";

/**
 * Marks the signed-in member as active when the app opens or comes back to the foreground.
 *
 * Middleware already records activity on page loads and navigations, but the phone app is often
 * resumed from the background straight onto the screen it was left on, with no navigation at all.
 * That open would not be counted, and "came back on another day" is exactly what the GrowthSync
 * goal measures. Throttled to once an hour per device; failures are silent because this is
 * measurement, never something a member should see. See src/lib/activity/record-activity.ts.
 */
export function ActivityHeartbeat() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    let inFlight = false;

    const readLast = (): string | null => {
      try {
        return window.localStorage.getItem(ACTIVITY_STORAGE_KEY);
      } catch {
        return null;
      }
    };

    const ping = async () => {
      if (cancelled || inFlight) return;
      if (document.visibilityState !== "visible") return;
      if (typeof navigator !== "undefined" && !navigator.onLine) return;
      if (!shouldRecordActivity(readLast())) return;
      inFlight = true;
      try {
        const { supabase } = await import("@/lib/supabase/client");
        const { error } = await supabase.rpc("record_member_activity");
        if (!error) {
          try {
            window.localStorage.setItem(ACTIVITY_STORAGE_KEY, currentActivityHour());
          } catch {
            // Private mode or blocked storage: we simply ping again next time.
          }
        }
      } catch {
        // Measurement only. Never surface this.
      } finally {
        inFlight = false;
      }
    };

    void ping();
    const onVisible = () => {
      if (document.visibilityState === "visible") void ping();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [isAuthenticated]);

  return null;
}
